// Parse a share-link query string back into the puzzle parameters the boot
// flow needs. Defensive: anything malformed → `{ kind: 'invalid' }` so the
// host can show an i18n error and fall through to the main menu rather than
// crashing or silently launching a wrong puzzle.

import type { Difficulty } from '../game/types.js';
import { GENERATOR_VERSION } from '../generator/Carver.js';
import { sanitizeFrom } from './encode.js';

const SEED_HEX_WIDTH = 8;
const SEED_HEX_RADIX = 16;
const VERSION_RADIX = 10;
const TIME_RADIX = 10;
const U32_MAX = 0xffffffff;

const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard'];

export interface DecodedShare {
  readonly kind: 'shared';
  readonly seed: number;
  readonly difficulty: Difficulty;
  readonly from: string | null;
  readonly timeMs: number | null;
}

export type DecodeResult =
  | DecodedShare
  | { readonly kind: 'none' }
  | { readonly kind: 'invalid'; readonly reason: 'invalid' | 'outdated' };

/**
 * Parse `search` (e.g. `?seed=…&d=…&v=…`) into a `DecodeResult`.
 *
 * - No share-related params at all → `{ kind: 'none' }`.
 * - Required params present but malformed → `{ kind: 'invalid', reason: 'invalid' }`.
 * - Version mismatch → `{ kind: 'invalid', reason: 'outdated' }`.
 * - Valid → `{ kind: 'shared', ... }` with sanitised optional fields.
 */
export function decodeShareUrl(search: string): DecodeResult {
  const params = new URLSearchParams(search);
  const seedRaw = params.get('seed');
  const difficultyRaw = params.get('d');
  const versionRaw = params.get('v');

  if (seedRaw === null && difficultyRaw === null && versionRaw === null) {
    return { kind: 'none' };
  }
  if (seedRaw === null || difficultyRaw === null || versionRaw === null) {
    return { kind: 'invalid', reason: 'invalid' };
  }

  const version = parseIntStrict(versionRaw, VERSION_RADIX);
  if (version === null) return { kind: 'invalid', reason: 'invalid' };
  if (version !== GENERATOR_VERSION) return { kind: 'invalid', reason: 'outdated' };

  const seed = parseSeed(seedRaw);
  if (seed === null) return { kind: 'invalid', reason: 'invalid' };

  const difficulty = parseDifficulty(difficultyRaw);
  if (difficulty === null) return { kind: 'invalid', reason: 'invalid' };

  const from = parseFrom(params.get('from'));
  const timeMs = parseTime(params.get('time'));

  return { kind: 'shared', seed, difficulty, from, timeMs };
}

function parseSeed(raw: string): number | null {
  if (raw.length === 0 || raw.length > SEED_HEX_WIDTH) return null;
  if (!/^[0-9a-fA-F]+$/.test(raw)) return null;
  const value = parseInt(raw, SEED_HEX_RADIX);
  if (!Number.isFinite(value) || value < 0 || value > U32_MAX) return null;
  return value >>> 0;
}

function parseDifficulty(raw: string): Difficulty | null {
  for (const d of DIFFICULTIES) {
    if (d === raw) return d;
  }
  return null;
}

function parseFrom(raw: string | null): string | null {
  if (raw === null) return null;
  const cleaned = sanitizeFrom(raw);
  return cleaned.length > 0 ? cleaned : null;
}

function parseTime(raw: string | null): number | null {
  if (raw === null) return null;
  const value = parseIntStrict(raw, TIME_RADIX);
  if (value === null || value < 0) return null;
  return value;
}

function parseIntStrict(raw: string, radix: number): number | null {
  if (raw.length === 0) return null;
  if (!/^[0-9]+$/.test(raw)) return null;
  const value = parseInt(raw, radix);
  return Number.isFinite(value) ? value : null;
}
